/**
 * Fret-scale-degree recognition — the melodic guitar rung.
 *
 * A I–IV–V–I cadence establishes the key; then a single scale degree plays.
 * The tonic (do) is lit on the neck as the `given` anchor; the user taps where
 * the heard degree sits, relative to that anchor. Single tap, auto-submit.
 *
 * This is the melodic counterpart to the harmonic guitar rungs (triads, CAGED,
 * diatonic chords): the user projects a *function they hear* onto a *location on
 * the neck*. Audio is built the same way the audio-only scale-degree exercise
 * builds it (cadence → target note); the fretboard prompt is built the same way
 * the diatonic-triad rung builds its board. See docs/design/guitar-harmony-v1.md.
 */

import { AudioPlanBuilder } from "./audio-plan.js";
import { Rng, type Exercise, type FretboardPrompt } from "./exercise.js";
import {
  appendCadence,
  appendModalContext,
  degreeOffset,
  normalizeMode,
  MODE_PATTERNS,
  MODE_DISPLAY_NAMES,
  type CadenceOptions,
  type KeyChoice,
  type Mode,
  type ModeOrAlias,
} from "./utils.js";
import { buildResolutionPlan } from "./generators/scale-degree.js";
import {
  STANDARD_TUNING,
  midiAt,
  positionsForMidi,
  type FretPosition,
} from "@music-app/theory-engine";

// ─── Params ─────────────────────────────────────────────────────────────────

export interface FretScaleDegreeParams {
  /** 1..7 — the scale degree the user must place. */
  degree: number;
  /**
   * Scale type. Major (ionian) and minor (aeolian) are scheduled curriculum
   * nodes; the five other diatonic modes are free-practice only.
   */
  mode?: FretScaleType;
  /**
   * How many octaves of the neck to accept the degree in (1..3). Session-level:
   * it widens the accepted-position set and the Listen box, but is not part of
   * the item identity (a card is the same card at any octave span).
   */
  octaves?: number;
}

/**
 * Scale type for the guitar scale-degrees rung. Major and minor are real
 * curriculum nodes; the five diatonic modes are free-practice (generic ids).
 */
export type FretScaleType =
  | "major"
  | "minor"
  | "dorian"
  | "phrygian"
  | "lydian"
  | "mixolydian"
  | "locrian";

/**
 * Curriculum item id for a guitar scale-degree card. The analog of
 * `drillItemId` for the ear-training scales drill: major and minor resolve to
 * their scheduled curriculum nodes so practice rolls up into node mastery; the
 * five modes are free-practice and use a generic (unscheduled) namespace, just
 * like the ear-training exotic scale types.
 */
export function fretScaleDegreeItemId(scaleType: FretScaleType, degree: number): string {
  const deg = Math.min(Math.max(degree, 1), 7);
  if (scaleType === "major") return `fret-scale-degrees-major::${deg}`;
  if (scaleType === "minor") return `fret-scale-degrees-minor::${deg}`;
  return `drill-fret-scale-degrees-${scaleType}::${deg}`;
}

// Movable-do syllables, derived per degree from the degree's alteration
// relative to the major scale — so every diatonic mode names its own degrees
// correctly (dorian 7 → "te", lydian 4 → "fi", locrian 5 → "se"), without a
// hardcoded array per mode.
const MAJOR_OFFSETS = [0, 2, 4, 5, 7, 9, 11] as const;
const NATURAL_SYLLABLES = ["do", "re", "mi", "fa", "sol", "la", "ti"] as const;
const LOWERED_SYLLABLES: Record<number, string> = { 2: "ra", 3: "me", 5: "se", 6: "le", 7: "te" };
const RAISED_SYLLABLES: Record<number, string> = { 4: "fi" };

function degreeSyllable(degree: number, mode: ModeOrAlias): string {
  const idx = (degree - 1) % 7;
  const natural = NATURAL_SYLLABLES[idx]!;
  const actual = MODE_PATTERNS[normalizeMode(mode)][idx]!;
  let diff = ((actual - MAJOR_OFFSETS[idx]!) % 12 + 12) % 12;
  if (diff > 6) diff -= 12; // normalise to -1 / 0 / +1
  if (diff < 0) return LOWERED_SYLLABLES[degree] ?? natural;
  if (diff > 0) return RAISED_SYLLABLES[degree] ?? natural;
  return natural;
}

/** Modes whose third is major take a major cadence label; the rest read minor. */
const MAJOR_FAMILY: ReadonlySet<Mode> = new Set<Mode>(["ionian", "lydian", "mixolydian"]);

// ─── Key helper (pitch-class → KeyChoice, cadence in the middle octave) ───────

const PC_NAMES = ["C","D♭","D","E♭","E","F","F♯","G","A♭","A","B♭","B"] as const;

function buildKeyFromMidi(tonicMidi: number, mode: ModeOrAlias): KeyChoice {
  const pc = ((tonicMidi % 12) + 12) % 12;
  const pcName = PC_NAMES[pc]!;
  const hasFlat = pcName.includes("♭");
  const hasSharp = pcName.includes("♯");
  const letter = pcName.replace(/[♭♯]/u, "");
  const accidental = hasFlat ? -1 : hasSharp ? 1 : 0;
  const accSym = hasFlat ? "♭" : hasSharp ? "♯" : "";
  const canonical = normalizeMode(mode);
  return {
    letter,
    accidental,
    mode: canonical,
    tonicMidi: 60 + pc, // normalise to the middle octave for the cadence
    name: `${letter}${accSym} ${MODE_DISPLAY_NAMES[canonical]}`,
  };
}

// ─── Generator ────────────────────────────────────────────────────────────────

export function generateFretScaleDegree(
  itemId: string,
  params: FretScaleDegreeParams,
  seed: number,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const rng = new Rng(seed);
  const mode: ModeOrAlias = params.mode ?? "major";
  const octaves = Math.min(Math.max(Math.trunc(params.octaves ?? 1), 1), 3);
  const targetSyllable = degreeSyllable(params.degree, mode);

  // Anchor the tonic on the low-E or A string in a comfortable position, so the
  // scale box fits on the neck. The anchor's pitch class is the key. As the
  // octave span grows, keep the anchor lower so the upper octaves stay on-neck.
  const anchorString = rng.pick([0, 1]);
  const anchorJitter = octaves === 1 ? 7 : octaves === 2 ? 5 : 3;
  const anchorFret = 3 + rng.int(anchorJitter); // 3..(2 + jitter)
  const anchorPos: FretPosition = { stringIndex: anchorString, fret: anchorFret };
  const anchorMidi = midiAt(STANDARD_TUNING, anchorPos);

  const key = buildKeyFromMidi(anchorMidi, mode);

  // Neck target: the heard degree above the anchor tonic. The tonic itself (do)
  // is placed at the octave above the anchor so it is a distinct, non-trivial
  // location rather than the lit reference dot.
  const neckOffset = params.degree === 1 ? 12 : degreeOffset(params.degree, mode);
  const baseTargetMidi = anchorMidi + neckOffset;

  // Accept every position of that exact pitch on the neck, in each requested
  // octave — all the places the degree lives above this tonic (mirrors the
  // interval rung's exact-MIDI set). Octaves widens the net across the neck.
  const acceptedPositions: FretPosition[] = [];
  for (let k = 0; k < octaves; k++) {
    acceptedPositions.push(...positionsForMidi(STANDARD_TUNING, baseTargetMidi + 12 * k));
  }

  // Listen mode focuses the scale box around the anchor; each added octave opens
  // the window a fifth higher up the neck (3 octaves ≈ the whole fingerboard).
  const fretWindow = {
    from: Math.max(0, anchorFret - 1),
    to: Math.min(STANDARD_TUNING.fretCount, anchorFret + 4 + 5 * (octaves - 1)),
  };

  const fretboard: FretboardPrompt = {
    tuning: "standard",
    given: [anchorPos],
    notesToPlace: 1,
    acceptedPositions,
    solution: acceptedPositions,
    listenFretWindow: fretWindow,
  };

  // Audio: establish the key, then the target degree an octave above the
  // (context-register) tonic — the same framing as the audio-only rung.
  // Ionian/aeolian get a functional cadence; the other modes get a neutral
  // tonic drone (appendModalContext) so the ear isn't biased toward major/minor.
  const builder = new AudioPlanBuilder();
  if (key.mode === "ionian" || key.mode === "aeolian") {
    appendCadence(builder, key, cadenceOpts);
  } else {
    appendModalContext(builder, key);
  }
  const audioTargetMidi = key.tonicMidi + 12 + degreeOffset(params.degree, mode);
  builder.addNote(audioTargetMidi, 1.4, 0.22, "target");
  builder.advance(1.5);
  const audio = builder.build();

  return {
    itemId,
    exerciseType: "fret-scale-degree-recognition",
    audio,
    prompt: `Tap ${targetSyllable} on the neck`,
    options: [],
    correctAnswerId: "",
    meta: {
      keyName: key.name,
      seed,
      explanation: `Scale degree ${params.degree} (${targetSyllable}) in ${key.name} — relative to the lit tonic.`,
      distractorCount: 0,
      tonicMidi: anchorMidi, // drives relative solfège on the dots
      tonalityMode: MAJOR_FAMILY.has(key.mode) ? "major" : "minor",
      teachAudio: buildResolutionPlan(key, params.degree),
      fretboard,
      playbackMidis: [audioTargetMidi],
    },
  };
}
